import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { LandingNav } from '../Landing/LandingNav';

export function AuthConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type') as 'signup' | 'email' | 'recovery' | 'invite';

      if (!tokenHash || !type) {
        setStatus('error');
        setError('Invalid confirmation link. Please request a new one.');
        return;
      }

      try {
        // Verify the OTP token
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type === 'signup' ? 'email' : type,
        });

        if (verifyError) {
          console.error('[AuthConfirmPage] Verification error:', verifyError);
          setStatus('error');
          setError(verifyError.message);
          return;
        }

        if (!data.user) {
          setStatus('error');
          setError('Verification failed. Please try again.');
          return;
        }

        console.log('[AuthConfirmPage] Email verified successfully');
        setStatus('success');

        // Check for pending subscription
        const { data: pendingSubscription } = await supabase
          .from('pending_subscriptions')
          .select('plan, interval')
          .eq('user_id', data.user.id)
          .is('claimed_at', null)
          .maybeSingle();

        if (pendingSubscription) {
          console.log('[AuthConfirmPage] Found pending subscription, redirecting to pricing...');
          // Mark as claimed
          await supabase
            .from('pending_subscriptions')
            .update({ claimed_at: new Date().toISOString() })
            .eq('user_id', data.user.id);

          // Short delay to show success state, then redirect
          setTimeout(() => {
            navigate(`/virtual-staging/pricing?plan=${pendingSubscription.plan}&interval=${pendingSubscription.interval}`);
          }, 1500);
        } else {
          // No pending subscription, go to dashboard
          setTimeout(() => {
            navigate('/');
          }, 1500);
        }
      } catch (err) {
        console.error('[AuthConfirmPage] Unexpected error:', err);
        setStatus('error');
        setError('An unexpected error occurred. Please try again.');
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="bg-alabaster text-charcoal min-h-screen">
      <LandingNav onGetStarted={() => navigate('/auth?mode=signup')} />

      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="bg-white border border-stone shadow-soft-xl rounded-4xl p-8 md:p-10 text-center">
            {status === 'verifying' && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
                    <Loader2 className="w-12 h-12 text-olive animate-spin" />
                  </div>
                </div>
                <h1 className="text-3xl font-serif font-light tracking-tight mb-4 text-charcoal">
                  Verifying your email...
                </h1>
                <p className="text-lg text-sand font-light">
                  Please wait while we confirm your account.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
                    <CheckCircle className="w-12 h-12 text-olive" />
                  </div>
                </div>
                <h1 className="text-3xl font-serif font-light tracking-tight mb-4 text-charcoal">
                  Email Confirmed!
                </h1>
                <p className="text-lg text-sand font-light">
                  Redirecting you now...
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="bg-terracotta/10 p-5 rounded-full border border-terracotta/20">
                    <AlertCircle className="w-12 h-12 text-terracotta" />
                  </div>
                </div>
                <h1 className="text-3xl font-serif font-light tracking-tight mb-4 text-charcoal">
                  Verification Failed
                </h1>
                <p className="text-lg text-sand font-light mb-6">
                  {error}
                </p>
                <button
                  onClick={() => navigate('/auth?mode=signup')}
                  className="bg-olive hover:bg-charcoal text-alabaster py-3 px-6 rounded-full font-medium transition-all duration-500"
                >
                  Back to Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
